import { ChevronLeftIcon, ChevronRightIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import {
	BlankCell,
	CalendarHeader,
	DayGrid,
	MonthNavButton,
	MonthTitle,
	WeekdayLetter,
	WeekdayRow,
} from '@pages/Trips/CreateTripFlow/CreateTripCalendar.styles'
import React, { useMemo, useState } from 'react'
import { tripCalendar } from '../core/calendar'
import { TripDays, calendarDayFill } from './core/presentation'
import { CalendarDay, CalendarOutsideDay, DayCalendarCard } from './EventCreateFlow.styles'

const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const NO_EVENTS: ReadonlySet<number> = new Set()

interface EventDayCalendarProps {
	days: TripDays
	selectedDayIndex: number
	onPickDay: (dayIndex: number) => void
	/** The heading naming the calendar. */
	labelledBy: string
}

/**
 * The When step's day picker: the itinerary calendar's pages — a month at a
 * time, or an undated trip's day-number chips under no weekday header — drawn
 * as the create-trip calendar card, each trip day in its destination's tint and
 * the picked one filled. Opens on the picked day's page; the chevrons turn
 * through the trip's pages and no further. Every page holds the tallest page's
 * rows, so the card never changes height while flipping.
 */
const EventDayCalendar: React.FC<EventDayCalendarProps> = ({
	days,
	selectedDayIndex,
	onPickDay,
	labelledBy,
}) => {
	const { startDate, dayCount, segments } = days
	const { dated, pages, selectedPage } = useMemo(
		() =>
			tripCalendar(startDate, dayCount, {
				selectedDay: selectedDayIndex,
				daysWithEvents: NO_EVENTS,
				// Unused: the cells take the create-trip calendar's tints, not the itinerary's colours.
				colorIndexOf: () => 0,
			}),
		[startDate, dayCount, selectedDayIndex],
	)
	const [page, setPage] = useState(selectedPage)

	const pageIndex = Math.min(page, pages.length - 1)
	const current = pages[pageIndex]
	if (!current) return null
	const unit = dated ? 'month' : 'days'
	const cellCount = Math.max(...pages.map(({ weeks }) => weeks.length)) * WEEKDAY_LETTERS.length
	const cells = current.weeks.flat()

	return (
		<DayCalendarCard role="group" aria-labelledby={labelledBy}>
			<CalendarHeader>
				<MonthNavButton
					type="button"
					aria-label={`Previous ${unit}`}
					disabled={pageIndex === 0}
					onClick={() => setPage(pageIndex - 1)}
				>
					<ChevronLeftIcon sx={{ fontSize: iconSizes.lg }} />
				</MonthNavButton>
				<MonthTitle aria-live="polite">{current.title}</MonthTitle>
				<MonthNavButton
					type="button"
					aria-label={`Next ${unit}`}
					disabled={pageIndex === pages.length - 1}
					onClick={() => setPage(pageIndex + 1)}
				>
					<ChevronRightIcon sx={{ fontSize: iconSizes.lg }} />
				</MonthNavButton>
			</CalendarHeader>

			{dated && (
				<WeekdayRow aria-hidden>
					{WEEKDAY_LETTERS.map((letter, index) => (
						<WeekdayLetter key={`${letter}-${index}`}>{letter}</WeekdayLetter>
					))}
				</WeekdayRow>
			)}

			<DayGrid>
				{Array.from({ length: cellCount }, (_, index) => {
					const cell = cells[index]
					if (!cell || cell.kind === 'blank') return <BlankCell key={cell?.key ?? `pad-${index}`} />
					if (cell.kind === 'outside')
						return <CalendarOutsideDay key={cell.key}>{cell.label}</CalendarOutsideDay>
					return (
						<CalendarDay
							key={cell.key}
							type="button"
							aria-label={cell.name}
							aria-pressed={cell.selected}
							$fill={calendarDayFill(segments, cell.dayIndex)}
							$selected={cell.selected}
							onClick={() => onPickDay(cell.dayIndex)}
						>
							{cell.label}
						</CalendarDay>
					)
				})}
			</DayGrid>
		</DayCalendarCard>
	)
}

export default EventDayCalendar
